import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import client, { clearAuthHeaders } from "@/api/client";

const authAxios = axios.create({
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

authAxios.interceptors.response.use((response) => {
  AUTH_HEADER_KEYS.forEach((header) => {
    const value = response.headers[header];
    if (value) localStorage.setItem(`auth_${header}`, value);
  });
  return response;
});

const AUTH_HEADER_KEYS = ["access-token", "client", "uid", "expiry", "token-type"];

function persistSessionFromResponse(response) {
  const headers = response.headers;
  AUTH_HEADER_KEYS.forEach((h) => {
    if (headers[h]) localStorage.setItem(`auth_${h}`, headers[h]);
  });

  const userData = response.data.data;
  localStorage.setItem("auth_user", JSON.stringify(userData));
  return userData;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const hasActiveSubscription = computed(() => user.value?.active_subscription === true);
  const isSuperAdmin = computed(() => user.value?.super_admin === true);
  const checkoutUrl = ref("https://pay.hotmart.com/K106102480U?checkoutMode=10");

  function setSubscription(info = {}) {
    if (info.checkout_url) checkoutUrl.value = info.checkout_url;
    if (user.value && typeof info.active === "boolean") {
      user.value = { ...user.value, active_subscription: info.active };
      localStorage.setItem("auth_user", JSON.stringify(user.value));
    }
  }

  async function initialize() {
    const storedUser = localStorage.getItem("auth_user");
    const accessToken = localStorage.getItem("auth_access-token");

    if (storedUser && accessToken) {
      try {
        user.value = JSON.parse(storedUser);
        const response = await client.get("/me");
        user.value = response.data.user;
        localStorage.setItem("auth_user", JSON.stringify(response.data.user));
        if (response.data.subscription) {
          setSubscription(response.data.subscription);
        }
      } catch (e) {
        clearAuthHeaders();
        user.value = null;
      }
    }
    initialized.value = true;
  }

  async function login(email, password) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.post("/auth/sign_in", { email, password });
      user.value = persistSessionFromResponse(response);
      return { success: true };
    } catch (e) {
      error.value = e.response?.data?.errors?.[0] || "Erro ao fazer login";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function register(data) {
    loading.value = true;
    error.value = null;
    try {
      const payload = {
        name: data.name,
        email: data.email,
        confirm_success_url: `${window.location.origin}/ativar-conta`,
      };

      if (data.password) {
        payload.password = data.password;
        payload.password_confirmation = data.passwordConfirmation;
      }

      const response = await authAxios.post("/auth", payload);

      if (response.status === 202 || response.data?.data?.requires_code) {
        return {
          success: true,
          requiresCode: true,
          email: response.data.data?.email || data.email,
          message: response.data.message,
        };
      }

      user.value = persistSessionFromResponse(response);
      return { success: true, requiresCode: false };
    } catch (e) {
      error.value =
        e.response?.data?.errors?.full_messages?.[0] ||
        e.response?.data?.errors?.[0] ||
        "Erro ao criar conta";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function requestAuthCode({ email, purpose, context = {} }) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.post("/api/v1/auth_codes", {
        email,
        purpose,
        context,
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (e) {
      const data = e.response?.data;
      error.value =
        data?.errors?.[0] ||
        data?.errors?.full_messages?.[0] ||
        data?.error ||
        "Não conseguimos enviar o código agora.";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function verifyAuthCode({ email, code, purpose, context = {} }) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.post("/api/v1/auth_codes/verify", {
        email,
        code,
        purpose,
        context,
      });

      user.value = persistSessionFromResponse(response);
      return { success: true, couple: response.data.couple };
    } catch (e) {
      error.value = e.response?.data?.errors?.[0] || "Código inválido ou expirado.";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function setPassword({ password, passwordConfirmation }) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.patch(
        "/auth",
        {
          password,
          password_confirmation: passwordConfirmation,
        },
        {
          headers: {
            "access-token": localStorage.getItem("auth_access-token"),
            client: localStorage.getItem("auth_client"),
            uid: localStorage.getItem("auth_uid"),
          },
        }
      );

      user.value = response.data.data || user.value;
      localStorage.setItem("auth_user", JSON.stringify(user.value));
      return { success: true };
    } catch (e) {
      error.value =
        e.response?.data?.errors?.full_messages?.[0] ||
        e.response?.data?.errors?.[0] ||
        "Não conseguimos salvar sua senha.";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      await authAxios.delete("/auth/sign_out", {
        headers: {
          "access-token": localStorage.getItem("auth_access-token"),
          client: localStorage.getItem("auth_client"),
          uid: localStorage.getItem("auth_uid"),
        },
      });
    } catch (e) {
      // Ignore errors on logout
    } finally {
      clearAuthHeaders();
      user.value = null;
    }
  }

  function setSession(userData, headers = {}) {
    AUTH_HEADER_KEYS.forEach((h) => {
      if (headers[h]) localStorage.setItem(`auth_${h}`, headers[h]);
    });
    user.value = userData;
    localStorage.setItem("auth_user", JSON.stringify(userData));
  }

  return {
    user,
    loading,
    error,
    initialized,
    isAuthenticated,
    hasActiveSubscription,
    isSuperAdmin,
    checkoutUrl,
    initialize,
    login,
    register,
    requestAuthCode,
    verifyAuthCode,
    setPassword,
    logout,
    setSession,
    setSubscription,
  };
});
